<template>
  <div class="pt-3 px-3 pb-4 fz-page">

    <feasibility-check-modal
        v-if="showNewCheck"
        :studies="studies"
        :studies-loaded="studiesLoaded"
        :initial-customer-id="customerId"
        @queued="onQueued"
        @closed="showNewCheck = false"
    ></feasibility-check-modal>

    <feasibility-result-modal
        v-if="openRequestId"
        :key="openRequestId"
        :request-id="openRequestId"
        :summary="openSummary"
        @updated="onResultUpdated"
        @closed="onResultClosed"
    ></feasibility-result-modal>

    <!-- header -->
    <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
      <div class="flex-grow-1" style="min-width: 0;">
        <h2 class="mb-1">Attribution Feasibility</h2>
        <div class="fz-muted">
          Will a lift study on this inventory have enough exposed and control conversions? Checks run in Snowflake and take 1–10 minutes.
          <a href="javascript:void(0)" class="ms-1" @click="toggleExplainer">{{ showExplainer ? 'Hide' : 'How is this decided?' }}</a>
        </div>
      </div>
      <div class="d-flex gap-2 flex-shrink-0">
        <button class="btn btn-light border" @click="loadChecks" :disabled="loading">
          <font-awesome-icon :icon="['fas', 'arrows-spin']" class="me-1" :class="{ 'fa-spin': loading }" />
          Refresh
        </button>
        <button class="btn btn-primary" @click="showNewCheck = true">
          <font-awesome-icon :icon="['fas', 'plus']" class="me-1" />
          New check
        </button>
      </div>
    </div>

    <!-- how it works -->
    <div v-if="showExplainer" class="fz-explainer mb-3">
      <div class="d-flex justify-content-between align-items-start mb-2">
        <div class="fw-semibold fz-explainer-title">How feasibility is decided</div>
        <button type="button" class="btn-close btn-sm" aria-label="Hide" @click="toggleExplainer"></button>
      </div>
      <p class="mb-3">
        A lift study only produces a number if enough people convert in both groups. The pipeline needs at least
        <strong>2 exposed converters</strong> and <strong>5 control converters in each control draw</strong>.
        This check estimates those counts <em>before</em> the study runs, by replaying the study's method on the
        same inventory in a period before the flight.
      </p>
      <div class="fz-explainer-grid">
        <div>
          <div class="fz-explainer-head">1 · The data it uses</div>
          <ul class="mb-0">
            <li><strong>Inventory:</strong> the campaign's frames and assets, and the devices our location data matched
              to them in a window before the flight (as long as the flight, up to 91 days).</li>
            <li><strong>Cars and other rotating inventory</strong> only have matches while a campaign runs on them, so the
              check looks back up to a year for a window where they were matched. If only some units were, it scales
              their counts up to the whole campaign.</li>
            <li><strong>Control group:</strong> devices that passed the same areas without being exposed, matched to the
              exposed devices on how active they are and how far they live from the nearest store.</li>
            <li><strong>Conversions:</strong> for store visits, devices seen inside the store polygons; for web, pixel
              fires matched by IP. If a pixel only went live recently, its own conversion rate is used instead.</li>
          </ul>
        </div>
        <div>
          <div class="fz-explainer-head">2 · How the verdict is set</div>
          <ul class="mb-0 list-unstyled">
            <li class="mb-1"><span class="fz-pill fz-pill-sm fz-pill-go me-1">Go</span> projected counts clear the floor.</li>
            <li class="mb-1"><span class="fz-pill fz-pill-sm fz-pill-review me-1">Review</span> the estimate falls short, but
              studies like it have come in high enough before, or its inventory history is thin. Don't decline;
              re-check once the flight's first weeks are in.</li>
            <li class="mb-1"><span class="fz-pill fz-pill-sm fz-pill-no me-1">Not feasible</span> it misses the floor even
              at the optimistic end, on solid inventory history.</li>
            <li class="mb-1"><span class="fz-pill fz-pill-sm fz-pill-info me-1">Needs pixel data</span> the pixel has no
              usable history; the result gives the conversion rate it would need.</li>
            <li><span class="fz-pill fz-pill-sm fz-pill-muted me-1">No inventory history</span> too few units were ever
              matched to estimate from.</li>
          </ul>
        </div>
        <div>
          <div class="fz-explainer-head">3 · How far to trust it</div>
          <p class="mb-2">Tested on 75 past studies (102 store sets and pixels), as if run before each flight:</p>
          <ul class="mb-2">
            <li>None of the studies that went on to produce a lift would have been declined.</li>
            <li>All 3 footfall studies that failed were flagged, and 32 of 34 web pixels that failed.</li>
          </ul>
          <p class="mb-0 fz-muted small">The counts are estimates projected to the flight, not results. The confidence
            interval shown is for information; it doesn't affect the verdict.</p>
        </div>
      </div>
    </div>

    <!-- summary tiles (click to filter) -->
    <div class="fz-stats mb-3">
      <button v-for="tile in tiles" :key="tile.key" type="button" class="fz-stat text-start"
              :class="{ active: verdictFilter === tile.key }"
              @click="verdictFilter = verdictFilter === tile.key ? 'ALL' : tile.key">
        <div class="fz-stat-label"><span class="fz-dot" :class="tile.dot"></span>{{ tile.label }}</div>
        <div class="fz-stat-value">{{ tile.count }}</div>
      </button>
    </div>

    <!-- filters + table -->
    <div class="card fz-card">
      <div class="card-body pb-2">
        <div class="row g-2 align-items-center">
          <div class="col-12 col-md-4">
            <select class="form-select" v-model="customerId" @change="loadChecks">
              <option :value="null">All customers</option>
              <option v-for="customer in model.customers" :key="customer.customerId" :value="customer.customerId">
                {{ customer.customerName }}
              </option>
            </select>
          </div>
          <div class="col-12 col-md-5">
            <div class="input-group">
              <span class="input-group-text bg-white"><font-awesome-icon :icon="['fas', 'magnifying-glass']" class="fz-muted" /></span>
              <input type="text" class="form-control" placeholder="Search study, campaign, check # or requester" v-model="search">
            </div>
          </div>
          <div class="col-12 col-md-3 text-md-end small fz-muted">
            <span v-if="activeCount">
              <span class="spinner-grow spinner-grow-sm text-primary me-1" style="width: .6rem; height: .6rem;"></span>
              {{ activeCount }} running · auto-refreshing
            </span>
            <span v-else-if="lastLoaded">Updated {{ timeAgo(lastLoaded, now) }}</span>
          </div>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0 fz-history">
          <thead>
          <tr>
            <th style="width: 150px;">Check</th>
            <th>Study / campaign</th>
            <th>Flight</th>
            <th>Result</th>
            <th class="text-end">Exposed<div class="fz-th-sub">devices</div></th>
            <th class="text-end">Location / pixel<div class="fz-th-sub">devices</div></th>
            <th class="text-end">Converters<div class="fz-th-sub">exposed · control/draw</div></th>
            <th class="text-end">Run time<div class="fz-th-sub">est. cost</div></th>
            <th style="width: 48px;"><span class="visually-hidden">Actions</span></th>
          </tr>
          </thead>
          <tbody>
          <tr v-if="loading && !checks.length">
            <td colspan="9" class="text-center fz-muted py-5">
              <span class="spinner-border spinner-border-sm me-2"></span>Loading checks…
            </td>
          </tr>
          <tr v-else-if="!visibleChecks.length">
            <td colspan="9" class="text-center py-5">
              <div class="fz-muted mb-2">{{ checks.length ? 'No checks match these filters.' : 'No feasibility checks yet.' }}</div>
              <button v-if="!checks.length" class="btn btn-sm btn-primary" @click="showNewCheck = true">
                <font-awesome-icon :icon="['fas', 'plus']" class="me-1" />Run the first check
              </button>
            </td>
          </tr>
          <tr v-for="row in visibleChecks" :key="row.requestId" class="fz-row" @click="openCheck(row)">
            <td>
              <div class="fw-semibold">#{{ row.requestId }}</div>
              <div class="small fz-muted" :title="formatDateTime(row.postedAt)">{{ timeAgo(row.postedAt, now) }}</div>
              <div class="small fz-muted text-truncate" style="max-width: 140px;" :title="row.requestedBy">{{ shortName(row.requestedBy) }}</div>
            </td>
            <td>
              <div class="d-flex align-items-center gap-2">
                <span class="fw-semibold">{{ subjectTitle(row) }}</span>
                <span v-if="typeLabel(row.studyType)" class="badge rounded-pill fz-type-badge">{{ typeLabel(row.studyType) }}</span>
              </div>
              <div class="small fz-muted">{{ [row.customerName, subjectDetail(row)].filter(Boolean).join(' · ') }}</div>
            </td>
            <td class="small text-nowrap">
              <template v-if="row.flight">
                {{ shortDate(row.flight.start) }} – {{ shortDate(row.flight.end) }}
                <div class="fz-muted">{{ row.flight.days }} days</div>
              </template>
              <span v-else class="fz-muted">—</span>
            </td>
            <td>
              <span class="fz-pill" :class="pillFor(row).css">
                <span v-if="isActive(row)" class="spinner-border spinner-border-sm me-1" style="width: .7rem; height: .7rem;"></span>
                {{ pillFor(row).label }}
              </span>
              <div v-if="isActive(row)" class="small fz-muted mt-1">{{ duration(row.postedAt, null, now) }}</div>
              <div v-else-if="row.status === 'ERROR'" class="small text-danger mt-1 text-truncate" style="max-width: 180px;" :title="row.errorMessage">{{ row.errorMessage }}</div>
            </td>
            <td class="text-end">{{ formatNumber(row.exposedDevices) }}</td>
            <td class="text-end">{{ formatNumber(row.targetDevices) }}</td>
            <td class="text-end text-nowrap">
              <template v-if="row.status === 'DONE' && row.exposedConverters != null">
                <span :class="convClass(row.exposedConverters, 2)">{{ formatNumber(row.exposedConverters, 1) }}</span>
                <span class="fz-muted"> · </span>
                <span :class="convClass(row.controlConvertersPerDraw, 5)">{{ formatNumber(row.controlConvertersPerDraw, 1) }}</span>
              </template>
              <span v-else class="fz-muted">—</span>
            </td>
            <td class="text-end text-nowrap small">
              <template v-if="runSeconds(row) !== null">
                <div>{{ formatSeconds(runSeconds(row)) }}</div>
                <div class="fz-muted" :title="costHint">{{ formatCost(runSeconds(row)) }}</div>
              </template>
              <span v-else class="fz-muted">—</span>
            </td>
            <td class="text-end">
              <button v-if="!isActive(row)" type="button" class="btn btn-sm btn-light border fz-rerun"
                      :title="row.status === 'ERROR' ? 'Retry this check' : 'Run this check again'"
                      :disabled="rerunning === row.requestId" @click.stop="rerun(row)">
                <span v-if="rerunning === row.requestId" class="spinner-border spinner-border-sm" style="width: .8rem; height: .8rem;"></span>
                <font-awesome-icon v-else :icon="['fas', 'rotate-right']" />
                <span class="visually-hidden">{{ row.status === 'ERROR' ? 'Retry' : 'Rerun' }}</span>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <div class="card-footer bg-white small fz-muted d-flex flex-wrap justify-content-between gap-2">
        <span>{{ visibleChecks.length }} of {{ checks.length }} checks</span>
        <span>Click a row for the full result. Go = clears the pipeline floor (2 exposed, 5 control converters per draw).</span>
      </div>
    </div>

    <div v-if="errorText" class="alert alert-warning mt-3">{{ errorText }}</div>
  </div>
</template>

<script>
import streetmetrics from '@/model';
import AttributionService from '../services/AttributionService';
import FeasibilityCheckModal from '../components/FeasibilityCheckModal';
import FeasibilityResultModal from '../components/FeasibilityResultModal';
import {
  CHECK_CREDIT_OVERHEAD, CHECK_CREDITS_PER_HOUR, USD_PER_CREDIT,
  duration, errorMessage, formatCost, formatDateTime, formatNumber, formatSeconds, isActive, pillFor, rerunBody, runSeconds,
  subjectDetail, subjectTitle, timeAgo, typeLabel, unwrap,
} from '../utils/feasibility';

const REFRESH_MS = 15000;

/** The explainer starts open until someone closes it (remembered per browser) */
function readExplainerPref() {
  try {
    return window.localStorage.getItem('feasibility.explainer') !== '0';
  } catch (e) {
    return true;
  }
}

export default {
  name: 'AttributionFeasibility',
  components: { FeasibilityCheckModal, FeasibilityResultModal },
  data() {
    return {
      model: streetmetrics.model,
      checks: [],
      loading: false,
      lastLoaded: null,
      now: Date.now(),
      customerId: null,
      search: '',
      verdictFilter: 'ALL',
      studies: [],
      studiesLoaded: false,
      showNewCheck: false,
      openRequestId: null,
      openSummary: null,
      refreshTimer: null,
      clockTimer: null,
      errorText: null,
      showExplainer: readExplainerPref(),
      rerunning: null,
      costHint: `Estimate from run time: ${CHECK_CREDITS_PER_HOUR} credits/h (Large warehouse) x ${CHECK_CREDIT_OVERHEAD} `
        + `(measured on the first checks) at $${USD_PER_CREDIT}/credit. Not a billed figure.`,
    };
  },
  computed: {
    activeCount() {
      return this.checks.filter(isActive).length;
    },
    tiles() {
      const count = (key) => this.checks.filter((row) => this.matchesVerdict(row, key)).length;
      return [
        { key: 'ALL', label: 'All checks', count: this.checks.length, dot: 'fz-dot-all' },
        { key: 'ACTIVE', label: 'In progress', count: count('ACTIVE'), dot: 'fz-dot-queued' },
        { key: 'FEASIBLE', label: 'Go', count: count('FEASIBLE'), dot: 'fz-dot-go' },
        { key: 'REVIEW', label: 'Review', count: count('REVIEW'), dot: 'fz-dot-review' },
        { key: 'FLAGGED', label: 'Not feasible / no data', count: count('FLAGGED'), dot: 'fz-dot-no' },
      ];
    },
    visibleChecks() {
      const terms = this.search.trim().toLowerCase().split(/\s+/).filter(Boolean);
      return this.checks.filter((row) => {
        if (!this.matchesVerdict(row, this.verdictFilter)) return false;
        if (!terms.length) return true;
        const text = [
          row.requestId, row.studyId, row.studyName, row.customerName, row.requestedBy,
          ...(row.campaigns || []).map((c) => `${c.campaignId} ${c.campaignRef || ''}`),
          ...(row.pixelIds || []),
        ].join(' ').toLowerCase();
        return terms.every((term) => text.includes(term));
      });
    },
  },
  async mounted() {
    this.clockTimer = setInterval(() => { this.now = Date.now(); }, 1000);
    const requestId = Number(this.$route.query.requestId);
    if (requestId) this.openRequestId = requestId;
    await this.loadChecks();
    this.loadStudies();
  },
  beforeUnmount() {
    clearTimeout(this.refreshTimer);
    clearInterval(this.clockTimer);
  },
  methods: {
    duration, formatCost, formatDateTime, formatNumber, formatSeconds, isActive, pillFor, runSeconds, subjectDetail, subjectTitle,
    timeAgo, typeLabel,
    matchesVerdict(row, key) {
      if (key === 'ALL') return true;
      if (key === 'ACTIVE') return isActive(row);
      if (key === 'FLAGGED') {
        return row.status === 'ERROR' || (row.status === 'DONE' && !['FEASIBLE', 'REVIEW'].includes(row.verdict));
      }
      return row.status === 'DONE' && row.verdict === key;
    },
    async loadChecks() {
      clearTimeout(this.refreshTimer);
      this.loading = true;
      this.errorText = null;
      try {
        this.checks = unwrap(await new AttributionService().listFeasibility(this.customerId)) || [];
        this.lastLoaded = new Date().toISOString();
        if (this.openRequestId) {
          this.openSummary = this.checks.find((row) => row.requestId === this.openRequestId) || this.openSummary;
        }
      } catch (error) {
        this.errorText = errorMessage(error, 'Could not load feasibility checks.');
      } finally {
        this.loading = false;
      }
      if (this.activeCount) this.refreshTimer = setTimeout(this.loadChecks, REFRESH_MS);
    },
    async loadStudies() {
      try {
        const studies = unwrap(await new AttributionService().list()) || [];
        await this.model.areCustomersLoaded;
        const names = {};
        (this.model.customers || []).forEach((customer) => { names[customer.customerId] = customer.customerName; });
        this.studies = studies
          .map((study) => ({ ...study, customerName: names[study.customerId] || '' }))
          .sort((a, b) => b.attributionId - a.attributionId);
      } catch (error) {
        console.log(error);
      } finally {
        this.studiesLoaded = true;
      }
    },
    onQueued(requestId) {
      this.model.toast.background = 'bg-green';
      this.model.toast.message = `Feasibility check #${requestId} queued. It will update here when it finishes.`;
      this.verdictFilter = 'ALL';
      this.loadChecks();
    },
    /** Queue the same check again (same study / campaigns / places / pixels / flight) */
    async rerun(row) {
      if (this.rerunning) return;
      this.rerunning = row.requestId;
      try {
        const service = new AttributionService();
        const original = unwrap(await service.getFeasibility(row.requestId));
        const queued = unwrap(await service.startFeasibility(rerunBody(original && original.request)));
        this.onQueued(queued.requestId);
      } catch (error) {
        this.model.toast.background = 'bg-red';
        this.model.toast.message = errorMessage(error, `Check #${row.requestId} could not be rerun.`);
      } finally {
        this.rerunning = null;
      }
    },
    openCheck(row) {
      this.openSummary = row;
      this.openRequestId = row.requestId;
      this.$router.replace({ query: { ...this.$route.query, requestId: row.requestId } });
    },
    onResultUpdated(check) {
      // the result modal polls a running check; refresh the table once it changes state
      const row = this.checks.find((r) => r.requestId === check.requestId);
      if (row && row.status !== check.status) this.loadChecks();
    },
    onResultClosed() {
      this.openRequestId = null;
      this.openSummary = null;
      const query = { ...this.$route.query };
      delete query.requestId;
      this.$router.replace({ query });
    },
    toggleExplainer() {
      this.showExplainer = !this.showExplainer;
      try {
        window.localStorage.setItem('feasibility.explainer', this.showExplainer ? '1' : '0');
      } catch (e) {
        // storage unavailable: the panel just won't remember its state
      }
    },
    convClass(value, threshold) {
      if (value === null || value === undefined) return '';
      return Number(value) >= threshold ? 'text-success fw-semibold' : 'text-danger fw-semibold';
    },
    shortName(email) {
      return email ? String(email).split('@')[0] : '';
    },
    shortDate(value) {
      if (!value) return '';
      const date = new Date(`${value}T00:00:00`);
      return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
    },
  },
};
</script>

<!-- global: the pills are shared with the feasibility modals -->
<style>
.fz-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}
.fz-pill-sm {
  padding: 1px 8px;
  font-size: 0.75rem;
}
.fz-pill-go { background: #e3f4e8; color: #1d6b38; }
.fz-pill-review { background: #fff3d6; color: #8a5a00; }
.fz-pill-no { background: #fbe4e1; color: #a2271a; }
.fz-pill-info { background: #e2effa; color: #1b5d93; }
.fz-pill-queued { background: #e8edf2; color: #052f49; }
.fz-pill-muted { background: #eef1f4; color: #5c6b78; }
.fz-type-badge {
  background: #eef1f4;
  color: #4a5966;
  font-weight: 500;
}
/* Nike's theme colors text-secondary teal (link-like); the feasibility views use a neutral grey */
.fz-muted {
  color: #6b7a87 !important;
}
</style>

<style scoped>
.fz-explainer {
  border: 1px solid #e3e8ed;
  border-radius: 10px;
  background: #f8fafb;
  padding: 16px 18px;
}
.fz-explainer-title {
  color: #052f49;
  font-size: 1.05rem;
}
.fz-explainer-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}
@media (max-width: 1000px) {
  .fz-explainer-grid { grid-template-columns: 1fr; }
}
.fz-explainer-head {
  font-weight: 600;
  color: #052f49;
  margin-bottom: 6px;
}
.fz-explainer ul {
  padding-left: 1.1rem;
}
.fz-explainer li {
  margin-bottom: 4px;
}
.fz-stats {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 900px) {
  .fz-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
.fz-stat {
  border: 1px solid #e3e8ed;
  border-radius: 10px;
  background: #fff;
  padding: 10px 14px;
  transition: border-color .15s, box-shadow .15s;
}
.fz-stat:hover {
  border-color: #b9c6d2;
}
.fz-stat.active {
  border-color: #052f49;
  box-shadow: 0 0 0 1px #052f49;
}
.fz-stat-label {
  font-size: 0.8rem;
  color: #6b7a87;
  display: flex;
  align-items: center;
}
.fz-stat-value {
  font-size: 1.6rem;
  font-weight: 600;
  color: #052f49;
  line-height: 1.2;
}
.fz-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
}
.fz-dot-all { background: #052f49; }
.fz-dot-queued { background: #8aa1b5; }
.fz-dot-go { background: #2f9e57; }
.fz-dot-review { background: #e0a100; }
.fz-dot-no { background: #d0473a; }
.fz-card {
  border: 1px solid #e3e8ed;
  border-radius: 10px;
  overflow: hidden;
}
.fz-history thead th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7a87;
  font-weight: 600;
  background: #f6f8fa;
  border-bottom: 1px solid #e3e8ed;
  vertical-align: bottom;
  white-space: nowrap;
}
.fz-th-sub {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
}
.fz-row {
  cursor: pointer;
}
.fz-history td {
  border-color: #eef1f4;
}
.fz-rerun {
  line-height: 1;
  padding: .3rem .45rem;
  opacity: .55;
}
.fz-row:hover .fz-rerun,
.fz-rerun:focus-visible {
  opacity: 1;
}
</style>
